import { useMemo, useState } from "react";
import { api, ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { SupervisorNotes, reviewBadge } from "../components/SupervisorNotes";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "../api/useQuery";
import { LazyMap } from "../components/LazyMap";
import { Photo } from "../components/Photo";
import type { MapMarker } from "../components/MapView";
import {
  fmtDateTime, fmtDistance, fmtDuration, fmtTime, trackEventName, verdictName,
} from "../components/format";
import type { PatrolDetail } from "../api/types";

/**
 * Карточка обхода. Сверху — четыре числа, по которым начальник за секунду
 * понимает, нормально ли прошёл обход; всё, что требует разбора, собрано
 * в отдельный блок, а не рассыпано по странице.
 */
export function PatrolPage() {
  const { id } = useParams<{ id: string }>();
  const { data, error, loading, reload } = useQuery<PatrolDetail>(id ? `/api/v1/patrols/${id}` : null);

  const markers = useMemo<MapMarker[]>(() => {
    if (!data) return [];
    const out: MapMarker[] = [];
    for (const r of data.nfc_reads) {
      if (r.lat === null || r.lon === null) continue;
      out.push({
        lat: r.lat,
        lon: r.lon,
        label: `${r.point_name ?? r.uid_hex} · ${verdictName[r.verdict] ?? r.verdict} · ${fmtTime(r.device_time)}`,
        tone: r.counted ? (r.verdict === "ok" ? "ok" : "warn") : "danger",
      });
    }
    for (const p of data.missed_points) {
      out.push({
        lat: p.lat,
        lon: p.lon,
        label: `${p.point_name} · не пройдена${p.miss_reason ? ` · ${p.miss_reason}` : ""}`,
        tone: "danger",
      });
    }
    return out;
  }, [data]);

  const gaps = useMemo<[number, number][][]>(
    () => (data?.gaps ?? []).map((g) => [[g.from_lon, g.from_lat], [g.to_lon, g.to_lat]]),
    [data],
  );

  if (loading) return <p className="notice">Загрузка карточки обхода…</p>;
  if (error) return <div className="error" role="alert">{error}</div>;
  if (!data) return null;

  const counted = data.nfc_reads.filter((r) => r.counted).length;
  const total = counted + data.missed_points.length;
  const attention = data.missed_points.length + data.gaps.length + data.events.length;

  return (
    <>
      <div className="page-head">
        <div>
          <Link to="/shifts" className="small">‹ Смены и обходы</Link>
          <h2 style={{ marginTop: 6 }}>{data.route_name ?? "Внеплановый обход"} {reviewBadge(data.review)}</h2>
          <p className="muted small">
            {data.inspector_name} · {fmtDateTime(data.started_at)}–{data.ended_at ? fmtTime(data.ended_at) : "идёт"}
            {" "}· {data.territory_name} · {data.device_model}
          </p>
        </div>
        <TrackDownload patrolId={data.id} />
      </div>

      {data.review === "annul" && (
        <div className="error" role="status">Обход аннулирован: {data.review_text}. В сводки и на карту активности не входит.</div>
      )}
      {data.pending_files > 0 && (
        <div className="notice">Синхронизирован частично: с устройства ещё догружаются фото ({data.pending_files}).</div>
      )}

      <div className="stats">
        <div className="stat">
          <div className="stat-value">{fmtDuration(data.started_at, data.ended_at)}</div>
          <div className="stat-label">длительность</div>
        </div>
        <div className="stat">
          <div className="stat-value">{fmtDistance(data.distance_m)}</div>
          <div className="stat-label">дистанция · {data.point_count} точек трека</div>
        </div>
        <div className={`stat${data.missed_points.length > 0 ? " danger" : ""}`}>
          <div className="stat-value">{total > 0 ? `${counted} из ${total}` : "—"}</div>
          <div className="stat-label">контрольных точек</div>
        </div>
        <div className={`stat${data.gaps.length > 0 ? " warn" : ""}`}>
          <div className="stat-value">{data.gaps.length}</div>
          <div className="stat-label">разрывов трека</div>
        </div>
      </div>

      <div className="split">
        <div className="split-detail" style={{ position: "static" }}>
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <LazyMap track={data.track} gaps={gaps} markers={markers} height={460} />
          </div>
          {!data.track && <p className="muted small">Трек не записан: точек меньше двух.</p>}
          {/* Дистанция считается по точкам с приемлемой точностью — иначе шум
              GPS завышает километраж. Это важно понимать читающему отчёт. */}
          <p className="muted small" style={{ margin: "8px 4px 0" }}>
            Дистанция — по точкам с точностью не хуже 50 м; выбросы сохранены, но не учтены.
          </p>
        </div>

        <div className="split-list" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {attention > 0 && (
            <div>
              <div className="section-title">Требует внимания</div>
              <div className="list">
                {data.missed_points.map((p) => (
                  <div className="row" key={p.id} style={{ display: "block" }}>
                    <strong>{p.point_name} не пройдена</strong>
                    <span className="row-sub">{p.miss_reason ?? "Отметки инспектора нет"}</span>
                    {p.miss_photos.length > 0 && (
                      <div className="photos" style={{ marginTop: 8 }}>
                        {p.miss_photos.map((photo) => <Photo key={photo} id={photo} />)}
                      </div>
                    )}
                  </div>
                ))}
                {data.gaps.map((g, i) => (
                  <div className="row" key={`gap-${i}`} style={{ display: "block" }}>
                    <strong>Разрыв {Math.round(g.minutes)} мин, {fmtTime(g.from)}–{fmtTime(g.to)}</strong>
                    <span className="row-sub">Смещение между краями разрыва: {fmtDistance(g.distance_m)}</span>
                  </div>
                ))}
                {data.events.map((e, i) => (
                  <div className="row" key={`ev-${i}`} style={{ display: "block" }}>
                    <strong>{trackEventName[e.kind] ?? e.kind}</strong>
                    <span className="row-sub">{fmtDateTime(e.device_time)}{e.detail && ` · ${e.detail}`}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="section-title">Контрольные точки</div>
            <div className="list">
              {data.nfc_reads.length === 0 && data.missed_points.length === 0 && (
                <div className="row muted">На маршруте нет контрольных точек</div>
              )}
              {data.nfc_reads.map((r) => (
                <div className="row" key={r.id}>
                  <span>
                    {r.point_name ?? r.uid_hex}
                    <span className="row-sub">
                      {verdictName[r.verdict] ?? r.verdict}
                      {r.distance_m !== null && ` · ${fmtDistance(r.distance_m)} от метки`}
                      {r.client_verdict !== r.verdict && (
                        <span style={{ color: "var(--warning)" }}>
                          {` · на телефоне: ${verdictName[r.client_verdict] ?? r.client_verdict}`}
                        </span>
                      )}
                    </span>
                  </span>
                  <span className={r.counted ? "muted" : ""} style={r.counted ? undefined : { color: "var(--danger)" }}>
                    {fmtTime(r.device_time)}
                  </span>
                </div>
              ))}
              {/* Точки плана без зачёта — здесь же: иначе «что осталось»
                  приходится вычитать из считываний в уме. */}
              {data.missed_points.map((p) => (
                <div className="row" key={`missed-${p.id}`}>
                  <span>
                    {p.point_name}
                    <span className="row-sub">{p.miss_reason ?? "Отметки инспектора нет"}</span>
                  </span>
                  <span style={{ color: "var(--danger)" }}>не пройдена</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <PatrolReviewBlock patrolId={data.id} review={data.review} onChanged={reload} />
    </>
  );
}

/** Выгрузка трека обхода: GPX для навигаторов и ГИС, GeoJSON для веб-карт. */
function TrackDownload({ patrolId }: { patrolId: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function get(format: "gpx" | "geojson" | "pdf") {
    setBusy(true);
    setError(null);
    try {
      const path = format === "pdf"
        ? `/api/v1/patrols/${patrolId}/report`
        : `/api/v1/patrols/${patrolId}/track?format=${format}`;
      await api.download(path, `patrol.${format}`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Не удалось выгрузить трек");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="filters" style={{ margin: 0, alignItems: "center" }}>
      <button disabled={busy} onClick={() => void get("pdf")}>Отчёт PDF</button>
      <span className="muted small">Трек:</span>
      <button className="secondary" disabled={busy} onClick={() => void get("gpx")}>GPX</button>
      <button className="secondary" disabled={busy} onClick={() => void get("geojson")}>GeoJSON</button>
      {error && <span className="error small" role="alert">{error}</span>}
    </div>
  );
}

function PatrolReviewBlock({ patrolId, review, onChanged }: {
  patrolId: string; review: PatrolDetail["review"]; onChanged: () => void;
}) {
  const { me } = useAuth();
  return (
    <section className="card" style={{ marginTop: 16 }}>
      <h3 style={{ margin: "0 0 10px" }}>Проверка начальником</h3>
      <SupervisorNotes path={`/api/v1/patrols/${patrolId}/notes`} review={review}
        canWrite={me?.role !== "inspector"} onChanged={onChanged} />
    </section>
  );
}
